import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CreateJobRequest } from "../api/types";

const YOUTUBE_URL_RE =
  /^https?:\/\/(?:www\.|m\.)?(?:youtu\.be\/[A-Za-z0-9_-]{6,}|youtube\.com\/(?:watch\?[^#\s]*v=[A-Za-z0-9_-]{6,}|shorts\/[A-Za-z0-9_-]{6,}))/;

// Only Turkish is offered: it pins Deepgram's `language=tr` so Whisper-Large
// transcribes faithfully instead of silently translating to English.
const LANGUAGE_OPTIONS = ["tr"] as const;
type LanguageOption = (typeof LANGUAGE_OPTIONS)[number];

export interface UrlFormProps {
  disabled?: boolean;
  onSubmit: (payload: CreateJobRequest) => void;
}

export function UrlForm({ disabled = false, onSubmit }: UrlFormProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [language, setLanguage] = useState<LanguageOption>("tr");
  const languageSelectId = useId();

  const urlValid = useMemo(() => YOUTUBE_URL_RE.test(url.trim()), [url]);
  const canSubmit = urlValid && !disabled;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      youtube_url: url.trim(),
      rights_confirmation: true,
      rights_type: "owned_content",
      target_language: language,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 rounded-2xl border border-stone-800 bg-stone-900/60 p-6 shadow-xl"
    >
      <div>
        <label
          htmlFor="youtube-url"
          className="block text-sm font-medium text-stone-200"
        >
          {t("urlForm.label")}
        </label>
        <div className="mt-1 flex gap-2">
          <input
            id="youtube-url"
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://www.youtube.com/watch?v=..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={disabled}
            className="block min-w-0 flex-1 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 placeholder-stone-500 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
          />
          <button
            type="button"
            disabled={disabled}
            aria-label={t("urlForm.pasteAriaLabel")}
            onClick={() => {
              const clipboard = navigator.clipboard;
              if (!clipboard?.readText) return;
              void clipboard.readText().then((text) => setUrl(text)).catch(() => {});
            }}
            className="shrink-0 rounded-lg border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-stone-200 hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("urlForm.paste")}
          </button>
        </div>
        {url && !urlValid && (
          <p className="mt-1 text-xs text-amber-400">
            {t("urlForm.invalidHint")}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor={languageSelectId}
          className="block text-sm font-medium text-stone-200"
        >
          {t("urlForm.languageLabel")}
        </label>
        <select
          id={languageSelectId}
          value={language}
          onChange={(event) =>
            setLanguage(event.currentTarget.value as LanguageOption)
          }
          disabled={disabled}
          className="mt-1 block w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {t(`urlForm.languageOption.${option}`)}
            </option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-stone-950 transition-colors hover:bg-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-400"
      >
        {t("urlForm.submit")}
      </button>
    </form>
  );
}
